import { useState, useEffect, useMemo } from 'react'
import { Loader, Edit2 } from 'lucide-react'
import { projectsApi } from '../lib/projectsApi'

/**
 * "Current Conditions" view.
 *
 * Per-site card showing the latest reading of each **selected** parameter
 * (filtered by the sidebar's `selectedParameters`) as a metric card on the
 * left, alongside a site photo/webcam panel on the right.
 *
 * Unselected parameters are not rendered — users pick what they want to
 * see from the sidebar checkboxes.
 */
export function CurrentConditionsView({
  sites = [],
  selectedParameters = [],
  dateRange = {},
  timezone = 'UTC',
  projectImage,
}) {
  const [latest, setLatest] = useState({})   // { [paramId]: { value, unit, ts } }
  const [loading, setLoading] = useState(false)

  // Group selected parameters by site. A param is "selected" if its id is in
  // selectedParameters (sidebar checkboxes). We walk the sites tree to keep
  // the site/sensor context for grouping/labelling.
  const sitesWithSelected = useMemo(() => {
    const selectedIds = new Set((selectedParameters || []).map(p => String(p.id)))
    if (selectedIds.size === 0) return []
    return sites
      .map(site => {
        const params = []
        ;(site.devices || []).forEach(dev =>
          (dev.sensors || []).forEach(sensor =>
            (sensor.parameters || []).forEach(p => {
              if (selectedIds.has(String(p.id))) {
                params.push({ ...p, _sensorName: sensor.name })
              }
            })
          )
        )
        return { ...site, _params: params }
      })
      .filter(s => s._params.length > 0)
  }, [sites, selectedParameters])

  // Flat list of param ids that drives the data-fetch effect — stringifying
  // keeps the dep stable across selectedParameters/sites re-renders.
  const paramIdsKey = useMemo(
    () => sitesWithSelected.flatMap(s => s._params.map(p => p.id)).join(','),
    [sitesWithSelected]
  )

  useEffect(() => {
    const allParams = sitesWithSelected.flatMap(s => s._params)
    if (allParams.length === 0) { setLatest({}); return }

    let cancelled = false
    setLoading(true)

    Promise.allSettled(
      allParams.map(p =>
        projectsApi
          .fetchGraphData(Number(p.id), { fromDate: dateRange.from, toDate: dateRange.to, timezone })
          .then(res => ({
            id: p.id,
            value: (res && typeof res === 'object' && !res.error) ? res.latestValue : null,
            unit: (res && res.unit) || p.unit || '',
            ts: (res && res.latestTimestamp) || null,
          }))
      )
    ).then(results => {
      if (cancelled) return
      const map = {}
      results.forEach(r => {
        if (r.status === 'fulfilled' && r.value && r.value.id != null) map[r.value.id] = r.value
      })
      setLatest(map)
      setLoading(false)
    }).catch(() => { if (!cancelled) setLoading(false) })

    return () => { cancelled = true }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paramIdsKey, dateRange.from, dateRange.to, timezone])

  const fmtVal = (paramId, paramUnit) => {
    const l = latest[paramId]
    if (!l || l.value === null || l.value === undefined) return { value: '—', unit: '' }
    const v = typeof l.value === 'number' ? Number(l.value.toFixed(2)) : l.value
    const u = l.unit || paramUnit || ''
    return { value: String(v), unit: u }
  }

  if (!sites || sites.length === 0) {
    return (
      <div className="bg-white border-2 border-[#358ECD]/30 rounded-lg p-10 text-center text-gray-500 text-sm">
        No sites available for this project.
      </div>
    )
  }

  if ((selectedParameters || []).length === 0) {
    return (
      <div className="bg-white border-2 border-[#358ECD]/30 rounded-lg p-10 text-center text-gray-500 text-sm">
        Select one or more parameters from the sidebar to view current conditions.
      </div>
    )
  }

  if (sitesWithSelected.length === 0) {
    return (
      <div className="bg-white border-2 border-[#358ECD]/30 rounded-lg p-10 text-center text-gray-500 text-sm">
        The selected parameters don't belong to any site in this project.
      </div>
    )
  }

  return (
    <div className="space-y-4">
      <h3 className="text-lg font-bold text-gray-800">Current Conditions</h3>

      {loading && (
        <div className="flex items-center gap-2 text-sm text-gray-500">
          <Loader className="w-4 h-4 animate-spin" /> Loading latest readings…
        </div>
      )}

      {sitesWithSelected.map(site => (
        <div key={site.id} className="bg-white border-2 border-[#358ECD]/30 rounded-lg">
          {/* Station header */}
          <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100">
            <h4 className="text-base font-semibold text-gray-700">{site.name}</h4>
            <div className="flex items-center gap-1">
              <button type="button" title="More options" className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded transition">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                  <circle cx="12" cy="5" r="2" /><circle cx="12" cy="12" r="2" /><circle cx="12" cy="19" r="2" />
                </svg>
              </button>
              <button type="button" title="Edit" className="p-1.5 text-gray-400 hover:text-[#358ECD] hover:bg-gray-100 rounded transition">
                <Edit2 className="w-4 h-4" />
              </button>
            </div>
          </div>

          {/* Body: metric cards (left) + site photo (right) */}
          <div className="p-4 flex flex-col lg:flex-row gap-4">
            <div className="lg:w-2/5 grid grid-cols-1 sm:grid-cols-2 gap-3 content-start">
              {site._params.map(p => {
                const { value, unit } = fmtVal(p.id, p.unit)
                return (
                  <div
                    key={p.id}
                    className="border border-gray-200 rounded-lg p-3 bg-white hover:border-[#358ECD]/40 transition-colors"
                  >
                    <div className="text-[11px] font-medium text-gray-500 uppercase tracking-wide truncate" title={p.name}>
                      {p.name}
                    </div>
                    <div className="mt-1 flex items-baseline gap-1">
                      <span className="text-2xl font-bold text-gray-900 leading-tight">{value}</span>
                      {unit && <span className="text-xs text-gray-500">{unit}</span>}
                    </div>
                  </div>
                )
              })}
            </div>

            {/* Site photo / webcam placeholder */}
            <div className="lg:w-3/5">
              <div className="bg-gray-100 rounded-lg overflow-hidden h-72 lg:h-full min-h-[280px] flex items-center justify-center">
                {projectImage ? (
                  <img src={projectImage} alt={`${site.name} site`} className="w-full h-full object-cover" />
                ) : (
                  <div className="text-center text-gray-400 text-sm px-6">
                    Connect a NexSens Environmental Webcam<br />or Edit to Add a Site Photo
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      ))}
    </div>
  )
}
